import { downloadDataAsZip } from "../Helpers/Functions/downloadAs"
import { hasValue, getValue } from "../Models/Client/FetchState"
import { downloadAllResponses } from "../Actions/API/Job/JobDownloadActions"
import { useDispatch } from "react-redux"
import {
    APIJobModel,
    APIJobListParams,
    JobStatus
} from "../Models/API/JobModel"
import { useAppState } from "./useReduxState"
import { useMemo, useEffect, useState, useCallback } from "react"
import { getJobResponse } from "../Actions/API/Job/JobActions"
import { UseAsyncCommand, useAsyncCommand } from "./useAsyncCommand"
import formatDate from "date-fns/format"
import { createWorkerPool } from "../Components/Common/Workers/workerPool"
import { extractDataUrlPath } from "../Models/API/DataModel"
import { getSignedUrl } from "../Actions/API/Data/DataActions"
import { MainThunkDispatch } from "../Reducers"
import { findDataUrlForDownload } from "../Helpers/Schema/Schema"
import {
    AnnotationFields,
    AnnotationFieldContent
} from "universal-schema/lib/1.0.0/annotation"
import { AppDownloadSettings } from "../Models/API/AppSettings"

export type DownloadType = "csv" | "json" | "flat_csv" | "response_data"

interface UseJobListDownloadResult {
    success: boolean
    willDownload: boolean
}

/**
 * React hook to asynchronously download jobs.
 */
export function useJobListDownload(
    appName: string,
    appID: string,
    targetJobs: APIJobModel[],
    args: Omit<APIJobListParams, "sortBy" | "orderBy">,
    type: DownloadType,
    imageDownloadPath: string[] | undefined,
    downloadSettings: AppDownloadSettings,
    allowDownloadResponseData?: boolean,
    isMessages: boolean = false
): UseAsyncCommand & UseJobListDownloadResult {
    const dispatch = useDispatch<MainThunkDispatch>()
    const [startDownload, setStartDownload] = useState(false)
    const [downloadSuccess, setDownloadSuccess] = useState(false)
    const outputs = useAppState(state => state.api.jobs.outputs)

    const completedJobs = targetJobs.filter(
        job => job.status === JobStatus.COMPLETED
    )

    const zipDownload =
        type === "response_data" &&
        (imageDownloadPath !== undefined || allowDownloadResponseData)

    const isReadyToDownload =
        zipDownload || targetJobs.length === 1
            ? completedJobs.every(job => hasValue(outputs[job.id]))
            : true

    const downloadAction = useCallback(async () => {
        setStartDownload(false)

        if (zipDownload) {
            const worker = createWorkerPool<"ZIP_DATA_WORKER">(1)
            let resolvedData
            if (imageDownloadPath !== undefined) {
                resolvedData = await Promise.all(
                    targetJobs
                        .map(job => ({
                            jobID: job.id,
                            imageUrl: findDataUrlForDownload(
                                getValue(outputs[job.id])?.response,
                                imageDownloadPath
                            )
                        }))
                        .filter(item => item.imageUrl !== undefined)
                        .map(async ({ jobID, imageUrl }) => {
                            const path = extractDataUrlPath(imageUrl!)
                            const name = `${jobID}.${imageUrl!
                                .split(".")
                                .pop()}`
                            if (path === undefined) {
                                return Promise.resolve({
                                    name,
                                    url: imageUrl!
                                })
                            }

                            return {
                                name,
                                url: await dispatch(
                                    getSignedUrl(imageUrl!, {
                                        jobID,
                                        type: "perJob"
                                    })
                                )
                            }
                        })
                )
            } else {
                resolvedData = await Promise.all(
                    targetJobs
                        .map(job => ({
                            jobID: job.id,
                            dataUrl: getValue(outputs[job.id])?.responseData
                        }))
                        .filter(item => typeof item.dataUrl === "string")
                        .map(async ({ jobID, dataUrl }) => {
                            const url = dataUrl as string
                            const path = extractDataUrlPath(url)
                            const name = `${jobID}.${url.split(".").pop()}`
                            if (path === undefined) {
                                return Promise.resolve({
                                    name,
                                    url
                                })
                            }

                            return {
                                name,
                                url: await dispatch(
                                    getSignedUrl(url, {
                                        jobID,
                                        type: "perJob"
                                    })
                                )
                            }
                        })
                )
            }

            const fileName = `${appName.replace(/\s/g, "_")}_data_${formatDate(
                new Date(),
                "yyyy-MM-dd_H-m-s"
            )}`
            return downloadDataAsZip(
                await worker.call("ZIP_DATA_WORKER", {
                    data: resolvedData.filter(
                        ({ url }) => url !== undefined
                    ) as { name: string; url: string }[]
                }),
                fileName
            )
        }

        let fileName = undefined
        if (
            targetJobs.length === 1 &&
            downloadSettings.customFileNameKeys.length
        ) {
            const annotations: AnnotationFields = (
                getValue(outputs[targetJobs[0].id])?.response as any
            ).annotations

            fileName =
                downloadSettings.customFileNameKeys
                    ?.map(key => {
                        const annotation: AnnotationFieldContent =
                            annotations[key]?.[0]
                        return annotation?.content ?? "vnf"
                    })
                    .join("_") +
                `_${appName}_data_${formatDate(new Date(), "yyyy-MM-dd_H-m-s")}`

            fileName = fileName.replaceAll(" ", "_").replaceAll("/", "_")
        }

        await dispatch(
            downloadAllResponses(
                appID,
                { onlyIfFeedback: isMessages, ...args },
                downloadSettings,
                type !== "json",
                targetJobs.map(j => j.id),
                fileName
            )
        )
    }, [
        appID,
        appName,
        args,
        dispatch,
        downloadSettings,
        imageDownloadPath,
        isMessages,
        outputs,
        targetJobs,
        type,
        zipDownload
    ])

    useEffect(() => {
        if (zipDownload || targetJobs.length === 1) {
            completedJobs.forEach(job => {
                if (outputs[job.id] === undefined) {
                    dispatch(getJobResponse(job.id))
                }
            })
        }
    }, [completedJobs, dispatch, outputs, targetJobs.length, zipDownload])

    useEffect(() => {
        if (startDownload && isReadyToDownload) {
            downloadAction().then(() => setDownloadSuccess(true))
        }
    }, [downloadAction, isReadyToDownload, startDownload, zipDownload])

    const action = useMemo(() => {
        if (startDownload) {
            return undefined
        }
        return async () => setStartDownload(true)
    }, [startDownload])

    return {
        ...useAsyncCommand(action),
        success: downloadSuccess,
        willDownload: !!zipDownload
    }
}
